import * as React from 'react'
import loadable from '@loadable/component'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'
import * as actions from '../../../actions/cart'
import * as actionsCheckout from '../../../actions/checkout'
import formatPrice from '../../../helpers/formatPrice'
import { parse } from 'query-string'
import {
  ProductCard,
  ProductGrid,
  Slider,
  SoldRibbon,
  ProductHeader,
  ProductName,
  Price,
  AddToCartButton,
  CounterSection,
  RemoveButton,
  AddButton,
  BuyItNowButton,
  ProductsCount,
  Shippable,
  ProductDescriptionParagraph,
  ArtDescription,
  ArtDescriptionLine,
  ArtDescriptionHeaderLine,
  ArtDescriptionPara,
  HiddenCheckbox,
  StyledCheckbox,
  CheckboxContainer,
  Checkmark,
  SubOptionContainer,
  BackToStoreButton,
  ProductSubOptions
} from './ProductDetailsStyledComponents'
import Cart from 'api/models/Cart'
import SubOption from 'api/models/SubOption'
import { Checkout } from 'redux/reducers/types'
import times from 'lodash/times'
import Store from 'api/models/Store'
import Product from '../../../api/models/Product'

const CheckoutModal = loadable(() => import('../../../pages/Store/Checkout/CheckoutModal'))

interface Props {
  product: Product
  storeId: string
  handleClose: () => void
}

interface RootState {
  cart: Cart
  checkout: Checkout
  activeStore: Store
}

interface CheckboxParams {
  checked: boolean
  onChange: () => void
  className?: string
  props?: any[]
}

function Checkbox({ checked, onChange, className, ...props }: CheckboxParams) {
  return (
    <CheckboxContainer className={className}>
      <HiddenCheckbox checked={checked} onChange={onChange} {...props} />
      <StyledCheckbox checked={checked}>
        <Checkmark viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12" />
        </Checkmark>
      </StyledCheckbox>
    </CheckboxContainer>
  )
}

interface SubOptionCheckboxProps {
  subOption: SubOption
  checked: boolean
  disabled: boolean
  onChange: () => void
}

function SubOptionCheckbox({ subOption, checked, onChange, disabled }: SubOptionCheckboxProps) {
  const price = subOption.sub_option.price > 0 ? `+${formatPrice(subOption.sub_option.price)}` : ''
  return (
    <div>
      <SubOptionContainer>
        {!disabled && <Checkbox checked={checked} onChange={onChange} />}
        <span style={{ paddingLeft: '10px' }}>{subOption.sub_option.name}</span>
        <span style={{ flexGrow: 1, textAlign: 'right' }}>{price}</span>
      </SubOptionContainer>
    </div>
  )
}

export default function ProductDetails({ product, storeId, handleClose }: Props) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const cart = useSelector((state: RootState) => new Cart(state.cart))
  const checkout = useSelector((state: RootState) => state.checkout)
  const activeStore = useSelector((state: RootState) => state.activeStore)

  const [checkoutModalShow, setCheckoutModalShow] = React.useState(false)

  const commentRef: React.RefObject<HTMLTextAreaElement> = React.useRef(null)

  const onBuyNow = () => {
    if (!productInCart) {
      dispatch(actions.addProduct(product))
      if (product.hasSubOptions()) {
        // Give the customer a chance to choose suboptions
        return
      }
    }
    dispatch(actionsCheckout.clearCheckout())
    dispatch(actionsCheckout.setCancelIndicator(false))
    dispatch(actionsCheckout.setPaymentStatus('pending', {}, 'checkout', storeId))
    setCheckoutModalShow(!checkoutModalShow)
  }

  const onCommentChange = () => {
    if (commentRef.current) {
      dispatch(actions.updateComments(product._id, commentRef.current.value))
    }
  }

  const productInCart = product._id && cart.getProduct(product._id)
  const addButtonActive =
    !product.isEnabledStock() || !productInCart || (productInCart && product.stock_quantity > productInCart.count)
  const showButtons = !product.isSoldOut()
  // TO DO: react GA event propably should be tracked on close checout modal
  let ct
  if (product.isWholesale()) {
    ct = parse(window.location.search).ct
    if (checkout.curatorToken !== ct) {
      dispatch(actionsCheckout.setCuratorToken(ct))
    }
  }

  const edition = product.getEdition()
  const subOptionList = product.getSubOptions()

  const isZeroTouch = activeStore.source === 'ZEROTOUCH'

  return (
    <ProductCard>
      <ProductGrid>
        <Slider images={product.getImages()} />
        <ProductHeader>
          <div style={{ display: 'flex' }}>
            <ProductName>{product.getName()}</ProductName>
            <Price isWholesale={product.isWholesale()}> {formatPrice(product.getPrice())} </Price>
          </div>
          {product.getDescription() && (
            <ProductDescriptionParagraph>{product.getDescription()}</ProductDescriptionParagraph>
          )}
        </ProductHeader>
        <SoldRibbon show={product.isSoldOut()}>
          <FormattedMessage id="product.soldOut" />
        </SoldRibbon>

        {showButtons &&
          (productInCart && productInCart.count ? (
            <CounterSection>
              <RemoveButton size="2.5rem" show onClick={() => dispatch(actions.removeProduct(product))} />
              <ProductsCount>{(productInCart && productInCart.count) || 0}</ProductsCount>
              <AddButton
                size="2.5rem"
                data-test-id="addButton"
                isactive={addButtonActive}
                onClick={() => addButtonActive && dispatch(actions.addProduct(product))}
              />
            </CounterSection>
          ) : (
            <AddToCartButton
              isactive={addButtonActive.toString()}
              onClick={() => addButtonActive && dispatch(actions.addProduct(product))}
            >
              <FormattedMessage id="store.addToCart" />
            </AddToCartButton>
          ))}
        {showButtons && (
          <BackToStoreButton onClick={handleClose}>
            <FormattedMessage id={isZeroTouch ? 'product.backToMenu' : 'product.backToStore'} />
          </BackToStoreButton>
        )}

        {product.hasSubOptions() && (
          <ProductSubOptions>
            {productInCart &&
              product.hasSubOptions() &&
              times(productInCart.count).map((prodIndex: number) => {
                const choices = productInCart.getSubOptionChoices()[prodIndex]
                return (
                  <div key={`prod${prodIndex}`}>
                    {productInCart.count > 1 && (
                      <p>
                        {intl.formatMessage({ id: 'item' })} {prodIndex + 1}
                      </p>
                    )}
                    <div>
                      {subOptionList.map((subOption: SubOption) => (
                        <SubOptionCheckbox
                          disabled={false}
                          key={`${subOption._id}-${prodIndex}`}
                          checked={choices.indexOf(subOption._id) > -1}
                          onChange={() =>
                            dispatch(
                              actions.updateSubOptionChoices(
                                productInCart._id,
                                productInCart.toggleSuboptionChoice(prodIndex, subOption)
                              )
                            )
                          }
                          subOption={subOption}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            {!productInCart &&
              subOptionList.map((subOption: SubOption) => (
                <SubOptionCheckbox
                  disabled={true}
                  key={subOption._id}
                  checked={false}
                  onChange={() => {}}
                  subOption={subOption}
                />
              ))}
            {productInCart && activeStore.source === 'ZEROTOUCH' && (
              <div>
                <p>
                  <FormattedMessage id="productDetails.comments" />
                </p>
                <textarea
                  ref={commentRef}
                  style={{ width: '100%' }}
                  value={productInCart.comments}
                  onChange={onCommentChange}
                />
              </div>
            )}
          </ProductSubOptions>
        )}
        {product.getIsArtwork() && (
          <ArtDescription>
            <ArtDescriptionPara>
              <ArtDescriptionHeaderLine>
                <FormattedMessage id="productDetails.productType" />:
              </ArtDescriptionHeaderLine>
              <ArtDescriptionLine>{product.getIsArtwork()}</ArtDescriptionLine>
            </ArtDescriptionPara>
            {edition && (
              <ArtDescriptionPara>
                <ArtDescriptionHeaderLine>
                  <FormattedMessage id="productDetails.editions" />:
                </ArtDescriptionHeaderLine>
                <ArtDescriptionLine>{edition.total}</ArtDescriptionLine>
              </ArtDescriptionPara>
            )}
            {product.getArtMedium() && (
              <ArtDescriptionPara>
                <ArtDescriptionHeaderLine>
                  <FormattedMessage id="productDetails.medium" />:
                </ArtDescriptionHeaderLine>
                <ArtDescriptionLine>{product.getArtMedium()}</ArtDescriptionLine>
              </ArtDescriptionPara>
            )}
            {product.getArtStyle() && (
              <ArtDescriptionPara>
                <ArtDescriptionHeaderLine>
                  <FormattedMessage id="productDetails.style" />:
                </ArtDescriptionHeaderLine>
                <ArtDescriptionLine>{product.getArtStyle()}</ArtDescriptionLine>
              </ArtDescriptionPara>
            )}
            {product.getDWidth() && (
              <ArtDescriptionPara>
                <ArtDescriptionHeaderLine>
                  <FormattedMessage id="productDetails.dimensions" />:
                </ArtDescriptionHeaderLine>
                <ArtDescriptionLine>{product.getDimensions()}</ArtDescriptionLine>
              </ArtDescriptionPara>
            )}
            {product.getWeight() && (
              <ArtDescriptionPara>
                <ArtDescriptionHeaderLine>
                  <FormattedMessage id="productDetails.weight" />:
                </ArtDescriptionHeaderLine>
                <ArtDescriptionLine>
                  {product.getWeight()}
                  {product.getWeightUnit()}
                </ArtDescriptionLine>
              </ArtDescriptionPara>
            )}
            {product.getAdditionalInfo() && (
              <ArtDescriptionPara>
                <ArtDescriptionHeaderLine>
                  <FormattedMessage id="productDetails.additionalInfo" />:
                </ArtDescriptionHeaderLine>
                <ArtDescriptionLine>{product.getAdditionalInfo()}</ArtDescriptionLine>
              </ArtDescriptionPara>
            )}
          </ArtDescription>
        )}
        {showButtons && productInCart && productInCart.count && (
          <BuyItNowButton onClick={onBuyNow}>
            <FormattedMessage id="product.buyItNow" />
          </BuyItNowButton>
        )}
        {!isZeroTouch && <Shippable show={product.isShippable()} product={product} />}
      </ProductGrid>
      {checkoutModalShow && (
        <CheckoutModal
          key="checkoutModal"
          isOpen={checkoutModalShow}
          storeId={storeId}
          merchantId={activeStore.getMerchantId()}
          // @ts-ignore
          ct={ct}
          toggle={() => setCheckoutModalShow(!checkoutModalShow)}
        />
      )}
    </ProductCard>
  )
}
